/**
 * Analytics event constants for tracking user interactions and performance
 * Organized by feature area for better maintainability
 */
export const TRACKING_EVENTS = {
  SLACK: { SETUP: "sofia_slack_setup" },
  DASHBOARD: {
    // Performance Events
    PAGE_LOADED: "dashboard_page_loaded",
    WIDGET_LOADED: "dashboard_widget_loaded",
    METRIC_QUERY_COMPLETED: "dashboard_metric_query_completed",
    METRIC_QUERY_FAILED: "dashboard_metric_query_failed",
    RUN_DETAILS_LOADED: "dashboard_run_details_loaded",

    // Widget Interactions
    WIDGET_CLICKED: "dashboard_widget_clicked",
    WIDGET_CONFIGURED: "dashboard_widget_configured",
    WIDGET_ADDED: "dashboard_widget_added",

    // Filter Interactions
    DATE_RANGE_CHANGED: "dashboard_date_range_changed",
    METADATA_FILTER_APPLIED: "dashboard_metadata_filter_applied",
    AGENT_FILTER_CHANGED: "dashboard_agent_filter_changed",
    DEDUPLICATION_TOGGLED: "dashboard_deduplication_toggled",

    // Navigation
    RUN_DETAILS_OPENED: "dashboard_run_details_opened",

    // Dashboard Management
    CREATED: "dashboard_created",

    // Advanced Features
    GLOBAL_OVERRIDE_ACTIVATED: "dashboard_global_override_activated",
    GLOBAL_OVERRIDE_DEACTIVATED: "dashboard_global_override_deactivated",
    POINT_SELECTION_MADE: "dashboard_point_selection_made",
    GRID_LAYOUT_CHANGED: "dashboard_grid_layout_changed",

    // Widget configuration
    NEW_TABLE_WIDGET_SELECTED: "new_table_widget_selected",
  },

  HUMAN_REVIEW: {
    // Labeling Events
    TRIAGE_MODE_ACTIVATED: "human_review_triage_mode_activated",
    EXPLANATION_MADE_ON_PLAYGROUND:
      "human_review_explanation_made_on_playground",
    NOTE_MADE_ON_PLAYGROUND: "human_review_note_made_on_playground",
    LABELING_TASK_CREATED: "human_review_labeling_task_created",
    LABELING_PROJECT_CREATED: "human_review_labeling_project_created",
    ANNOTATION_MADE: "human_review_annotation_made",
    GROUND_TRUTH_UPDATED: "human_review_ground_truth_updated",

    // Metric Health Section
    HUMAN_AGREEMENT_CARD_CLICKED: "human_review_human_agreement_card_clicked",
    RECOMMENDED_ACTION_CLICKED: "human_review_recommended_action_clicked",

    // Metric Studio Events
    EXPERIMENT_RUN: "human_review_experiment_run",
    METRIC_SAVED: "human_review_metric_saved",

    // Performance Events
    HUMAN_REVIEW_PAGE_LOADED: "human_review_page_loaded",
    HUMAN_REVIEW_WIDGET_LOADED: "human_review_widget_loaded",
    METRICS_STUDIO_PAGE_LOADED: "human_review_metrics_studio_page_loaded",
    METRICS_STUDIO_WIDGET_LOADED: "human_review_metrics_studio_widget_loaded",
    PROMPT_IMPROVED: "human_review_prompt_improved",
  },

  UPLOADED_CONVERSATIONS: {
    // Event values retain their established names for analytics continuity.
    // Page Events
    PAGE_LOADED: "monitoring_page_loaded",
    PAGE_VIEWED: "monitoring_page_viewed",

    // Table Performance Events
    TABLE_LOADED: "monitoring_table_loaded",
    TABLE_LOAD_TIME: "monitoring_table_load_time",
    TABLE_QUERY_COMPLETED: "monitoring_table_query_completed",
    TABLE_QUERY_FAILED: "monitoring_table_query_failed",
    COUNT_QUERY_COMPLETED: "monitoring_count_query_completed",
    COUNT_QUERY_FAILED: "monitoring_count_query_failed",

    // Search and Filter Events
    SEARCH_PERFORMED: "monitoring_search_performed",
    FILTER_APPLIED: "monitoring_filter_applied",
    FILTER_CLEARED: "monitoring_filter_cleared",
    DATE_RANGE_CHANGED: "monitoring_date_range_changed",
    METADATA_FILTER_APPLIED: "monitoring_metadata_filter_applied",
    METRIC_FILTER_APPLIED: "monitoring_metric_filter_applied",
    METRIC_VALUE_FILTER_APPLIED: "monitoring_metric_value_filter_applied",
    METRIC_VALUE_FILTER_REMOVED: "monitoring_metric_value_filter_removed",
    METRIC_VALUE_FILTERS_CLEARED: "monitoring_metric_value_filters_cleared",

    // Table Interactions
    TABLE_SORTED: "monitoring_table_sorted",
    TABLE_PAGINATED: "monitoring_table_paginated",
    TABLE_PAGE_CHANGED: "monitoring_table_page_changed",
    TABLE_ROWS_PER_PAGE_CHANGED: "monitoring_table_rows_per_page_changed",
    TABLE_ROW_CLICKED: "monitoring_table_row_clicked",
    TABLE_REFRESHED: "monitoring_table_refreshed",

    // Overview Metrics
    OVERVIEW_METRICS_LOADED: "monitoring_overview_metrics_loaded",
    OVERVIEW_METRIC_CLICKED: "monitoring_overview_metric_clicked",
    // Conversations page summary -> Sofia handoff. The legacy event value is
    // retained for PostHog continuity; payloads carry assistant_brand:"sofia".
    CONVERSATIONS_SUMMARY_DISCUSS_WITH_COVI:
      "monitoring_conversations_summary_discuss_with_covi",

    // Configuration
    CONFIGURE_METRICS_CLICKED: "monitoring_configure_metrics_clicked",
    COLUMN_VISIBILITY_CHANGED: "monitoring_column_visibility_changed",

    // CSV export
    DATA_EXPORTED_CSV: "monitoring_data_exported_csv",
  },

  OVERVIEW: {
    PAGE_VIEWED: "overview_page_viewed",
    DOCS_CLICKED: "overview_docs_clicked",
    API_DOCS_CLICKED: "overview_api_docs_clicked",
    CICD_CLICKED: "overview_cicd_clicked",
    API_KEY_CLICKED: "overview_api_key_clicked",
    SEARCH_FOCUSED: "overview_search_focused",
  },

  // Pre-auth account-creation events. The form is our React UI calling
  // Clerk's APIs — not Clerk-hosted — so we can track the whole flow.
  // FAILED vs. SUBMITTED lets the funnel distinguish drop-off from bounce.
  SIGNUP: {
    PAGE_VIEWED: "signup_page_viewed",
    GOOGLE_OAUTH_CLICKED: "signup_google_oauth_clicked",
    FORM_FIRST_INPUT: "signup_form_first_input",
    CREDENTIALS_SUBMITTED: "signup_credentials_submitted",
    CREDENTIALS_FAILED: "signup_credentials_failed",
    VERIFICATION_PAGE_VIEWED: "signup_verification_page_viewed",
    VERIFICATION_SUBMITTED: "signup_verification_submitted",
    VERIFICATION_FAILED: "signup_verification_failed",
    VERIFICATION_RESEND_CLICKED: "signup_verification_resend_clicked",
    VERIFICATION_CHANGE_EMAIL_CLICKED:
      "signup_verification_change_email_clicked",
  },

  // First-touch signup flow — feeds the new-user activation funnel.
  // Covers the plan + Stripe checkout step so the funnel starts at the
  // very first page the user sees post-sign-up.
  SIGNUP_WELCOME: {
    PAGE_VIEWED: "signup_welcome_page_viewed",
    PLAN_STEP_SKIPPED: "signup_welcome_plan_step_skipped",
    PLAN_SUBMITTED: "signup_welcome_plan_submitted",
    CHECKOUT_STARTED: "signup_welcome_checkout_started",
    CHECKOUT_COMPLETED: "signup_welcome_checkout_completed",
    MODAL_CLI_CLICKED: "signup_welcome_modal_cli_clicked",
    MODAL_DOCS_CLICKED: "signup_welcome_modal_docs_clicked",
    MODAL_CONNECT_AGENT_CLICKED: "signup_welcome_modal_connect_agent_clicked",
  },

  // Guided in-product onboarding flow (/[slug]/onboarding/*). A 4-step ladder
  // — add agent → choose test method → describe/upload a conversation →
  // launch the first simulation. Every event also carries step_number +
  // step_name so a single uniform funnel works in PostHog.
  ONBOARDING: {
    STARTED: "onboarding_started",
    AGENT_COMPLETED: "onboarding_agent_completed",
    TEST_METHOD_VIEWED: "onboarding_test_method_viewed",
    TEST_METHOD_SELECTED: "onboarding_test_method_selected",
    DESCRIBE_VIEWED: "onboarding_describe_viewed",
    DESCRIBE_SUBMITTED: "onboarding_describe_submitted",
    UPLOAD_VIEWED: "onboarding_upload_viewed",
    UPLOAD_FILE_SELECTED: "onboarding_upload_file_selected",
    UPLOAD_TRANSCRIBED: "onboarding_upload_transcribed",
    METRICS_GENERATED: "onboarding_metrics_generated",
    SIMULATION_LAUNCHED: "onboarding_simulation_launched",
    ABANDONED: "onboarding_abandoned",
  },

  // Post-trial billing lifecycle (Settings → Billing card, /home trial banner,
  // usage widget). The actual cancel/change happens inside Stripe's hosted
  // portal, so the client signal is the *intent*: which portal flow the user
  // opened (cancel / plan change / payment update / invoices) and from where.
  // The completed outcome (status → canceled, tier change) is read from the
  // Stripe data warehouse.
  BILLING: {
    SETTINGS_VIEWED: "billing_settings_viewed",
    PORTAL_OPENED: "billing_portal_opened",
    PORTAL_OPEN_FAILED: "billing_portal_open_failed",
    BANNER_DISMISSED: "billing_banner_dismissed",
    CONVERT_CTA_CLICKED: "billing_convert_cta_clicked",
  },

  SIMULATE: {
    RUN_LAUNCHED: "simulate_run_launched",
    // Fires when the simulate page opens; has_prefill + prefilled_from_run_id
    // mark the "intent to re-run" entry into the recursive-improvement loop.
    PAGE_OPENED: "simulate_page_opened",

    // Evaluation launch flow (EvaluationPage / EvaluationLauncher)
    EVALUATION_PAGE_VIEWED: "evaluation_page_is_viewed",
    EVALUATION_LAUNCHED: "evaluation_launched",
    EVALUATION_SUCCESS: "evaluation_success",
    EVALUATION_ERROR: "evaluation_error",
    EVALUATION_SCHEDULED: "evaluation_scheduled",

    // API code-preview panel on the launcher
    API_PREVIEW_OPENED: "api_preview_opened",
    API_PREVIEW_COPIED: "api_preview_copied",
    API_PREVIEW_LANGUAGE_CHANGED: "api_preview_language_changed",
  },

  AGENTS: {
    PAGE_VIEWED: "agents_page_viewed",
    CREATED: "agent_created",
    SAVED: "agent_saved",
    RENAMED: "agent_renamed",
    DELETED: "agent_deleted",
    CLONED: "agent_cloned",
    SIMULATION_LAUNCH_CLICKED: "agent_simulation_launch_clicked",
    PAGE_CREATE_BUTTON: "agents_page_create_button",
    FILTER_APPLIED: "agent_filter_applied",
    FILTER_CLEARED: "agent_filter_cleared",
  },

  PERSONAS: {
    PAGE_VIEWED: "personas_page_viewed",
    CREATED: "persona_created",
    SAVED: "persona_saved",
    RENAMED: "persona_renamed",
    DELETED: "persona_deleted",
    CLONED: "persona_cloned",
  },

  METRICS: {
    CREATE_CLICKED: "create_metric_clicked",
    CREATE_TYPE_SELECTED: "create_metric_type_selected",
    CATALOG_ENTRY_ADDED: "metric_catalog_entry_added",
    CREATED: "metric_created",
    SAVED: "metric_saved",
    RENAMED: "metric_renamed",
    DELETED: "metric_deleted",
    CLONED: "metric_cloned",
    TEST_METRIC_BUTTON_CLICKED: "test_metric_button_clicked",

    // Metrics list / route navigation + interactions
    PAGE_VIEWED: "metrics_page_viewed",
    ALL_PAGE_VIEWED: "metrics_all_page_viewed",
    GROUPING_CHANGED: "metrics_grouping_changed",
    VIEW_MODE_CHANGED: "metrics_view_mode_changed",
    COLLAPSE_ALL: "metrics_collapse_all",
    EXPAND_ALL: "metrics_expand_all",
    DETAIL_OPENED: "metric_detail_opened",
    TARGET_CONDITION_CREATED: "metric_target_condition_created",
  },

  ALERTS: {
    PAGE_VIEWED: "alerts_page_viewed",
  },

  TEST_SET: {
    CREATION_METHOD_SELECTED: "test_set_creation_method_selected",
    CREATED: "test_set_created",
    CREATION_FAILED: "test_set_creation_failed",
    SAVED: "test_set_saved",
    DISCARDED: "test_set_discarded",
    DELETED: "test_set_deleted",
    RENAMED: "test_set_renamed",
    CLONED: "test_set_cloned",
    CSV_DOWNLOADED: "test_set_csv_downloaded",

    // List page + create entry point
    LIST_PAGE_VIEWED: "test_sets_page_viewed",
    CREATE_CLICKED: "create_test_set_clicked",

    // Create flow + editor mutation failures
    TEXT_INPUT_SUBMITTED: "test_set_text_input_submitted",
    CLONE_FAILED: "test_set_clone_failed",
    DELETE_FAILED: "test_set_delete_failed",
    NAME_UPDATE_FAILED: "test_set_name_update_failed",
  },

  TEST_SET_EDITOR: {
    COPILOT_MESSAGE_SENT: "test_set_editor_copilot_message_sent",
    COPILOT_ACTION_PROPOSED: "test_set_editor_copilot_action_proposed",
    COPILOT_ACTION_APPROVED: "test_set_editor_copilot_action_approved",
    COPILOT_ACTION_DISCARDED: "test_set_editor_copilot_action_discarded",
    COPILOT_PARSE_ERROR: "test_set_editor_copilot_parse_error",
    COPILOT_REVERT: "test_set_editor_copilot_revert",
  },

  PERFORMANCE: {
    COMPONENT_LOAD_TIME: "component_load_time",
    TIME_ON_PAGE: "time_on_page",
  },

  RUNS: {
    RESULTS_VIEWED: "run_results_viewed",
    SUMMARY_FEEDBACK: "run_summary_feedback",
    // Run Summary card → Sofia handoff. Legacy event values stay for continuity.
    // only, never the summary text.
    SUMMARY_DISCUSS_WITH_COVI: "run_summary_discuss_with_covi",
    // Simulated conversations page summary → Sofia handoff. PII-safe: timeframe + counts
    // only, never the summary text.
    SIMULATIONS_SUMMARY_DISCUSS_WITH_COVI:
      "simulations_summary_discuss_with_covi",
    // Recursive-improvement loop: re-run surfaces on the run-detail page.
    RESIMULATE_CLICKED: "run_resimulate_clicked",
    ROWS_RESIMULATED: "run_rows_resimulated",
    OUTPUT_VIEWED: "run_output_viewed",

    // Runs list pages
    PAGE_VIEWED: "runs_page_viewed",
    SCHEDULED_PAGE_VIEWED: "scheduled_runs_page_viewed",

    // Run results CSV export
    CSV_EXPORT: "csv_export",
  },

  // Sofia (the in-product agent; legacy module path src/modules/app-covi) usage analytics.
  // Every event retains surface:"covi" and adds assistant_brand:"sofia" so the
  // panel's streams are never confused with the older test-set-editor copilot.
  // PII rule: NEVER send message text or prompts — only message_length, mirroring
  // MESSAGE_FEEDBACK. The lifecycle pair (PANEL_OPENED/CLOSED, MESSAGE_SENT →
  // REPLY_RECEIVED/REPLY_FAILED, ACTION_PROPOSED → CONFIRMED → EXECUTED/FAILED)
  // is what powers the DAU/WAU, sessions, messages-per-session, and action-funnel
  // insights in the PostHog usage dashboard.
  COPILOT: {
    MESSAGE_FEEDBACK: "copilot_message_feedback",
    // Panel lifecycle. `via` on OPENED = toggle | shortcut | auto_open | prompt;
    // on CLOSED = close_button | shortcut. Together they bound a usage session.
    PANEL_OPENED: "copilot_panel_opened",
    PANEL_CLOSED: "copilot_panel_closed",
    // One user turn. `message_length` only (never the text). `via` = composer |
    // slash_command | history_recall | seed.
    MESSAGE_SENT: "copilot_message_sent",
    // Assistant turn outcome. REPLY_RECEIVED carries latency_ms (send→reply),
    // reply_length, and action_count; REPLY_FAILED carries latency_ms + reason.
    REPLY_RECEIVED: "copilot_reply_received",
    REPLY_FAILED: "copilot_reply_failed",
    // Proposed → confirmed (manual | drive) → executed | failed; dismissed is a
    // terminal opt-out. All carry the action `kind` so the funnel breaks down by
    // capability (launch_run, create_persona, delete_metric, navigate, …).
    ACTION_PROPOSED: "copilot_action_proposed",
    ACTION_CONFIRMED: "copilot_action_confirmed",
    ACTION_EXECUTED: "copilot_action_executed",
    ACTION_FAILED: "copilot_action_failed",
    ACTION_DISMISSED: "copilot_action_dismissed",
    // Speed-dial slash command applied to the composer (`command` = "/diagnose").
    SLASH_COMMAND_USED: "copilot_slash_command_used",
    // "New conversation" cleared the thread (`message_count` = items discarded).
    CONVERSATION_RESET: "copilot_conversation_reset",
    // A past conversation was loaded back into the active slot.
    CONVERSATION_LOADED: "copilot_conversation_loaded",
    // A saved conversation was deleted from history.
    CONVERSATION_DELETED: "copilot_conversation_deleted",
  },

  REPORTS: {
    SAVED: "report_saved",
    PUBLISHED: "report_published",
    DETAIL_PAGE_VIEWED: "report_detail_page_viewed",
    DELETED: "report_deleted",
    SHARE_LINK_COPIED: "report_share_link_copied",
  },

  RUNS_FILTER: {
    SEARCH_APPLIED: "runs_filter_search_applied",
    DATE_RANGE_APPLIED: "runs_filter_date_range_applied",
    STATUS_ADDED: "runs_filter_status_added",
    STATUS_REMOVED: "runs_filter_status_removed",
    STATUS_BULK_UPDATED: "runs_filter_status_bulk_updated",
    TEMPLATE_ADDED: "runs_filter_template_added",
    TEMPLATE_REMOVED: "runs_filter_template_removed",
    TEMPLATE_BULK_UPDATED: "runs_filter_template_bulk_updated",
    PERSONA_ADDED: "runs_filter_persona_added",
    PERSONA_REMOVED: "runs_filter_persona_removed",
    PERSONA_BULK_UPDATED: "runs_filter_persona_bulk_updated",
    AGENT_ADDED: "runs_filter_agent_added",
    AGENT_REMOVED: "runs_filter_agent_removed",
    AGENT_BULK_UPDATED: "runs_filter_agent_bulk_updated",
    METRIC_ADDED: "runs_filter_metric_added",
    METRIC_REMOVED: "runs_filter_metric_removed",
    METRIC_BULK_UPDATED: "runs_filter_metric_bulk_updated",
    METRIC_VALUE_ADDED: "runs_filter_metric_value_added",
    METRIC_VALUE_REMOVED: "runs_filter_metric_value_removed",
    METRIC_VALUE_BULK_UPDATED: "runs_filter_metric_value_bulk_updated",
    TAG_ADDED: "runs_filter_tag_added",
    TAG_REMOVED: "runs_filter_tag_removed",
    TAGS_UPDATED: "runs_filter_tags_updated",
    MUTATION_ADDED: "runs_filter_mutation_added",
    MUTATION_REMOVED: "runs_filter_mutation_removed",
    MUTATION_BULK_UPDATED: "runs_filter_mutation_bulk_updated",
    TEST_SET_ADDED: "runs_filter_test_set_added",
    TEST_SET_REMOVED: "runs_filter_test_set_removed",
    TEST_SET_BULK_UPDATED: "runs_filter_test_set_bulk_updated",
    TEST_CASE_ADDED: "runs_filter_test_case_added",
    TEST_CASE_REMOVED: "runs_filter_test_case_removed",
    TEST_CASE_BULK_UPDATED: "runs_filter_test_case_bulk_updated",
    SCHEDULED_JOB_ADDED: "runs_filter_scheduled_job_added",
    SCHEDULED_JOB_REMOVED: "runs_filter_scheduled_job_removed",
    SCHEDULED_JOB_BULK_UPDATED: "runs_filter_scheduled_job_bulk_updated",
    SORT_CHANGED: "runs_filter_sort_changed",
    CLEAR_ALL: "runs_filter_clear_all",
  },

  TEMPLATES: {
    PAGE_VIEWED: "templates_page_viewed",
    PAGE_CREATE_BUTTON: "template_page_create_button",
  },

  // Public share-link page views (read-only shared run / multi-run report
  // surfaces reached via a share link, plus result clicks within them).
  SHARE: {
    PAGE_VIEWED: "share_page_viewed",
    MULTI_RUN_PAGE_VIEWED: "share_multi_run_page_viewed",
    RUN_DETAILS_RESULT_CLICKED: "run_details_result_clicked",
  },

  // Widget-level metric events (used by MetricWidgetContent)
  WIDGET_METRIC_LOADED: "widget_metric_loaded",
  WIDGET_METRIC_ERROR: "widget_metric_error",
} as const
